import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@/components/synapse/Icon';
import { DatePicker } from '@/components/synapse/DatePicker';
import { Select } from '@/components/synapse/Select';
import { Button } from '@/components/ui/button';
import { useCan } from '@/hooks/usePermissions';
import { useLabCatalogue, useLabReportUrl, useLabReports } from '@/features/lab/useLab';
import { translateApiError } from '@/lib/apiError';
import { formatFileSize } from '@/features/lab/labClient';
import type { LabReportRow } from '@/types/lab';

/**
 * Every report received, newest first.
 *
 * A separate surface from the worklist on purpose. The worklist answers "what is
 * still outstanding?" and is organised by order; this answers "find me that
 * report" and is organised by document — the same report reached two different
 * ways, because those are two different jobs.
 *
 * Search is limited to patient name and accession, and that is a deliberate
 * constraint rather than an omission: the API ORs its search arms, so a single
 * unindexed arm would collapse the whole disjunction into a sequential scan.
 * Test is a dropdown filter over `test_code` equality for the same reason.
 */

const PAGE_LIMIT = 50;

export function LabReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();

  const [searchParams, setSearchParams] = useSearchParams();
  // Lives in the URL rather than in state so the scoped view is linkable and
  // survives a reload — this is where the patient card's "See all" lands.
  const patientId = searchParams.get('patientId') ?? '';

  const [query, setQuery] = useState('');
  const [testCode, setTestCode] = useState('');
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [page, setPage] = useState(0);

  // Deactivated and typed tests appear in results but are absent from an
  // active-only catalogue, so the filter could name neither. The management
  // screen's superset is the right list for a filter over historical documents.
  const catalogue = useLabCatalogue({ includeInactive: true });
  const reports = useLabReports({
    q: query.trim() || undefined,
    patientId: patientId || undefined,
    testCode: testCode || undefined,
    from: range.from || undefined,
    to: range.to || undefined,
    limit: PAGE_LIMIT,
    offset: page * PAGE_LIMIT,
  });

  // Named from the rows themselves — the page has no patient lookup of its own,
  // and every row in a scoped result is the same person.
  const scopedPatientName = patientId ? (reports.data?.[0]?.patientName ?? null) : null;

  function clearPatientScope() {
    searchParams.delete('patientId');
    setSearchParams(searchParams, { replace: true });
    setPage(0);
  }

  const rows = useMemo(() => reports.data ?? [], [reports.data]);
  // The endpoint returns a page, not a count. Rather than show a total that
  // means "on this page", paging is driven by whether the page came back full.
  const hasMore = rows.length >= PAGE_LIMIT;

  function resetToFirstPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6">
      <header>
        <h1 className="text-xl font-semibold tracking-[-0.02em] text-ink">
          {t('lab.reportsTitle')}
        </h1>
        <p className="text-[13px] text-muted">{t('lab.reportsSubtitle')}</p>
      </header>

      {patientId && (
        // A filter the operator cannot see is a filter they will blame the data
        // for. Shown as a removable chip, not a silent narrowing of the results.
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-line-2 bg-paper-2 px-3 py-1 text-[12.5px] text-ink">
            {t('lab.reports.scopedToPatient', {
              name: scopedPatientName ?? t('lab.reports.thisPatient'),
            })}
            <button
              type="button"
              onClick={clearPatientScope}
              aria-label={t('lab.reports.clearPatientScope')}
              className="inline-flex h-11 w-11 items-center justify-center text-muted hover:text-ink"
            >
              <Icon name="x" size={14} />
            </button>
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="text-[12px] font-medium text-muted">{t('lab.reports.searchLabel')}</span>
          <span className="flex h-11 items-center gap-2 rounded-md border border-line-2 bg-paper px-3">
            <Icon name="search" size={15} className="shrink-0 text-muted" />
            <input
              value={query}
              onChange={(e) => resetToFirstPage(setQuery)(e.target.value)}
              aria-label={t('lab.reports.searchLabel')}
              placeholder={t('lab.searchPlaceholder')}
              className="h-full flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-muted-2"
            />
          </span>
        </label>

        <label className="flex flex-col gap-1" htmlFor="lab-report-test">
          <span className="text-[12px] font-medium text-muted">{t('lab.reports.testFilter')}</span>
          <Select
            id="lab-report-test"
            value={testCode}
            onChange={(e) => resetToFirstPage(setTestCode)(e.target.value)}
            className="w-52"
          >
            <option value="">{t('lab.reports.allTests')}</option>
            {(catalogue.data?.tests ?? []).map((def) => (
              <option key={def.code} value={def.code}>
                {def.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1" htmlFor="lab-report-from">
          <span className="text-[12px] font-medium text-muted">{t('lab.dateFrom')}</span>
          <DatePicker
            id="lab-report-from"
            value={range.from}
            max={range.to || undefined}
            onChange={(from) => resetToFirstPage(setRange)({ ...range, from: from ?? '' })}
          />
        </label>
        <label className="flex flex-col gap-1" htmlFor="lab-report-to">
          <span className="text-[12px] font-medium text-muted">{t('lab.dateTo')}</span>
          <DatePicker
            id="lab-report-to"
            value={range.to}
            min={range.from || undefined}
            onChange={(to) => resetToFirstPage(setRange)({ ...range, to: to ?? '' })}
          />
        </label>
      </div>

      {reports.isPending && (
        <ul className="flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <li
              key={i}
              className="h-[62px] animate-pulse rounded-md border border-line-2 bg-paper-2"
            />
          ))}
        </ul>
      )}

      {reports.isError && (
        <div className="rounded-md border border-line-2 bg-paper px-4 py-6 text-center">
          <p className="text-[13.5px] text-ink">{t('lab.reportsLoadFailed')}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void reports.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {!reports.isPending && !reports.isError && rows.length === 0 && (
        <p className="rounded-md border border-line-2 bg-paper px-4 py-8 text-center text-[13.5px] text-muted">
          {t('lab.emptyReports')}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id}>
              <ReportRow
                row={row}
                canOpen={can('lab.results.read')}
                onOpenOrder={() => navigate(`/lab/${row.orderId}`)}
              />
            </li>
          ))}
        </ul>
      )}

      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="secondary"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            {t('common.previous')}
          </Button>
          <span className="text-[12.5px] text-muted">{t('lab.reports.page', { page: page + 1 })}</span>
          <Button variant="secondary" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
            {t('common.next')}
          </Button>
        </div>
      )}
    </div>
  );
}

function ReportRow({
  row,
  canOpen,
  onOpenOrder,
}: {
  row: LabReportRow;
  canOpen: boolean;
  onOpenOrder: () => void;
}) {
  const { t } = useTranslation();
  const mint = useLabReportUrl();
  const [error, setError] = useState<string | null>(null);

  async function open() {
    setError(null);
    try {
      const { url } = await mint.mutateAsync(row.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(translateApiError(err, { fallback: t('lab.detail.openFailed') }));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-line-2 bg-paper px-3 py-2.5">
      <span className="flex min-w-[150px] flex-1 flex-col">
        <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-ink">
          {row.patientName}
        </span>
        <span className="text-[12px] text-muted">{row.patientMrn}</span>
      </span>

      <span className="min-w-[120px] flex-1 text-[12.5px] text-ink-2">{row.testName}</span>

      <button
        type="button"
        onClick={onOpenOrder}
        aria-label={t('lab.openOrder', { accession: row.accessionNumber })}
        className="font-mono text-[12.5px] tabular-nums text-muted underline-offset-2 hover:text-ink hover:underline"
      >
        {row.accessionNumber}
      </button>

      <span className="text-[12px] tabular-nums text-muted">
        {row.reportDate ?? row.uploadedAt.toLocaleDateString()}
      </span>

      {row.labName && <span className="text-[12px] text-muted">{row.labName}</span>}

      <span className="text-[12px] tabular-nums text-muted-2">{formatFileSize(row.byteSize)}</span>

      {/* Superseded and removed reports stay listed, dimmed. A clinician may
          have acted on one, so it remains part of the record. */}
      {row.supersedesDocumentId && (
        <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11px] text-muted">
          {t('lab.supersededBadge')}
        </span>
      )}
      {row.deletedAt && (
        <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11px] text-muted">
          {t('lab.removedBadge')}
        </span>
      )}

      {canOpen ? (
        <Button size="sm" variant="secondary" onClick={() => void open()} disabled={mint.isPending}>
          <Icon name="arrow_up_right" size={13} />
          {t('lab.openReport')}
        </Button>
      ) : (
        <span className="text-[11.5px] text-muted-2">{t('lab.patient.reportOnFile')}</span>
      )}

      {error && <span className="w-full text-[12px] text-destructive">{error}</span>}
    </div>
  );
}
